import Head from 'next/head'
import { GetStaticProps, GetStaticPropsContext, NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'

import PanelWrapper from '~/panels/components/PanelWrapper/PanelWrapper'
import ROUTES from '~/shared/constants/routes.const'
import Student from '~/panels/student/components/Student'
import { Breakpoint, useCheckBreakpoint } from '~/utils'
import { COMMON_TRANSLATION_MODULE_NAMES, DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES } from '~/shared/layouts/constants'
import { DefaultLayout } from '~/shared/layouts/DefaultLayout'
import { STUDENT_PANEL_TABS } from '~/panels/constants/student-panel'
import { useGetStudentExistsQuery } from '~/redux/apis/student-api'
import { usePageTitle } from '~/utils/tags'
import AuthGuard from '~/components/AuthGuard'
import { useAppSelector } from '~/redux/hooks'
import { isAuthenticated } from '~/auth/slices/auth.slice'

const StudentIndexPage: NextPage = () => {
  const { t } = useTranslation('studentPanel')
  const title = t('title')
  const pageTitle = usePageTitle(title)
  const router = useRouter()

  const authenticated = useAppSelector(isAuthenticated)
  const { data: isStudentExists } = useGetStudentExistsQuery(undefined, { skip: !authenticated })
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.SM })

  useEffect(() => {
    if (isStudentExists && !isMobile) router.replace(ROUTES.STUDENT_PANEL_MY_TUTOR)
  }, [isMobile, router, isStudentExists])

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta content="noindex" name="robots" />
      </Head>
      <DefaultLayout>
        <AuthGuard>
          <PanelWrapper disabled={!isStudentExists} tabs={STUDENT_PANEL_TABS} title={title}>
            <Student isMobile={isMobile} />
          </PanelWrapper>
        </AuthGuard>
      </DefaultLayout>
    </>
  )
}

export const getStaticProps: GetStaticProps = async ({ locale }: GetStaticPropsContext) => ({
  props: {
    ...(await serverSideTranslations(locale as string, [
      ...DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES,
      ...COMMON_TRANSLATION_MODULE_NAMES,
      'adminPanel',
      'booking',
      'error',
      'tutorOnDemand',
      'panels',
      'studentPanel',
    ])),
  },
})

export default StudentIndexPage
